import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Mail, Palette, LayoutTemplate, MousePointerClick, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { setSeoMetadata } from '../lib/seo';
import { SIGNATURE_TEMPLATES } from '../tools/signature-builder/templates';
import { markSignatureBuilderIntent } from '../tools/signature-builder/pendingIntent';

const FEATURES = [
  { icon: LayoutTemplate, title: 'Professional templates', body: 'Pick a layout and it already looks right - no fiddling with table widths or inline CSS.' },
  { icon: Palette, title: 'Match your brand', body: 'Set your own primary, secondary, and accent colors, add your logo, headshot, and a banner.' },
  { icon: MousePointerClick, title: 'Custom fields & buttons', body: 'Add extra links, badges, or call-to-action buttons - reorder everything exactly how you want it.' },
  { icon: Mail, title: 'Works everywhere', body: 'Copy it straight into Gmail, Outlook, or any email client, or grab the raw HTML.' },
];

const PREVIEW_SAMPLE_FIELDS = {
  fullName: 'Natalie Lynch',
  title: 'Sales Representative',
  company: 'Your Company',
  phone: '202-555-0141',
  email: 'natalie.lynch@example.com',
  website: 'example.com',
  linkedin: 'natalielynch',
  logoUrl: 'https://ui-avatars.com/api/?name=Your+Co&background=FF6B00&color=fff&size=128&bold=true',
};
const PREVIEW_SAMPLE_COLORS = { primary: '#FF6B00', secondary: '#666666', accent: '#000000' };
const PREVIEW_TEMPLATE_IDS = ['modern', 'executive', 'glass'];

const EmailSignaturesPage = () => {
  const { siteName } = useSiteSettings();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setSeoMetadata({
      title: 'Free Email Signature Builder',
      description:
        'Build a professional email signature with your logo, colors, and contact info - sign in free to save it. '
        + (siteName ? `Built by ${siteName}.` : ''),
      keywords: 'email signature generator, email signature builder, free email signature maker',
      canonicalPath: '/tools/email-signatures',
    });
  }, [siteName]);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/account/signatures/new');
      return;
    }
    markSignatureBuilderIntent();
    navigate('/register');
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Link
            to="/tools"
            className="inline-flex items-center gap-1.5 text-[#888680] hover:text-[#FF6B00] text-sm mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            All free tools
          </Link>
          <span className="inline-block px-3 py-1 rounded-full border border-[#FF6B00]/30 text-[#FF6B00] text-xs font-semibold tracking-wide mb-4">
            OFFICE UTILITIES
          </span>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#FF6B00]/10 mb-6">
            <Mail className="w-8 h-8 text-[#FF6B00]" />
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Email Signature Builder</h1>
          <p className="text-[#888680] text-lg max-w-xl mx-auto mb-8">
            Build a polished, on-brand email signature in minutes. Pick a template, drop in your
            details and logo, then copy it straight into your inbox. Free for any registered account.
          </p>
          <button
            type="button"
            onClick={handleGetStarted}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#FF6B00] hover:bg-[#e05f00] text-white font-semibold rounded-full text-lg transition-colors"
            data-testid="email-signatures-get-started"
          >
            Get Started <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-[#666] text-xs mt-3">
            {isAuthenticated ? 'Opens the builder.' : 'Free account required - takes under a minute.'}
          </p>
        </div>
      </section>

      {/* Live template previews */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-white font-bold text-2xl text-center mb-2">A few of the templates</h2>
          <p className="text-[#888680] text-center mb-10 max-w-xl mx-auto">
            Every template adapts to your colors and content. Here&apos;s a sample with placeholder details.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {SIGNATURE_TEMPLATES.filter((t) => PREVIEW_TEMPLATE_IDS.includes(t.id)).map((template) => (
              <div key={template.id} className="bg-white rounded-2xl p-5 overflow-hidden">
                <div className="scale-[0.55] origin-top-left h-[150px] overflow-hidden">
                  <div dangerouslySetInnerHTML={{ __html: template.render(PREVIEW_SAMPLE_FIELDS, PREVIEW_SAMPLE_COLORS) }} />
                </div>
                <p className="text-[#1a1c1b] font-semibold text-sm mt-2">{template.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature highlights */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-[#302f2c] border border-[#4a3520] rounded-2xl p-6 text-center">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#FF6B00]/10 text-[#FF6B00] mb-4">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="text-white font-semibold mb-1.5">{f.title}</h3>
              <p className="text-[#888680] text-sm">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-gradient-to-br from-[#FF6B00]/10 to-transparent border border-[#FF6B00]/30 rounded-2xl p-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-[#FF6B00]" />
              <h2 className="text-white font-bold text-xl">Ready to build yours?</h2>
            </div>
            <p className="text-[#dfddd6] text-sm mb-6">
              It&apos;s free with any account - no credit card, no catch.
            </p>
            <button
              type="button"
              onClick={handleGetStarted}
              className="inline-flex items-center gap-1.5 px-6 py-3 bg-[#FF6B00] hover:bg-[#e05f00] text-white font-semibold rounded-full transition-colors"
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EmailSignaturesPage;
